import { artistNameAfterArtistChange } from '../lib/artistName';
import type { ProductHomeSectionOption, ProductHomeSections } from '../hooks/useProductHomeSections';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  ImageGalleryField,
  ImageUploadField,
  Input,
  RadioGroup,
  RequiredMark,
  toast,
} from '@/components/ui';
import { collectFormErrors } from '../lib/formErrors';
import { useImageUploadQueue } from '@/lib/upload';
import { PolicySelect } from '@/features/policies/components/PolicySelect';
import { EarnPolicySelect } from '@/features/mileage/components/EarnPolicySelect';
import { ShippingFeePolicySelect } from '@/features/shippingFee/components/ShippingFeePolicySelect';
import { useDefaultShippingFeePolicyQuery } from '@/features/shippingFee/hooks/useShippingFee';
import {
  BENEFIT_EXCLUSIONS,
  emptyProductFormValues,
  productFormSchema,
  SEARCH_KEYWORD_MAX,
  PRODUCT_LOCALES,
  type ProductFormValues,
  type CurrencyPrices,
  type ShippingFeeType,
} from '../schemas/productSchema';

/** QA-456 혜택 제외 이름 — 고도몰 [혜택 제외]와 같은 말. */
const BENEFIT_EXCLUSION_LABELS: Record<(typeof BENEFIT_EXCLUSIONS)[number], string> = {
  GRADE_DISCOUNT: '회원 추가 할인',
  GRADE_OVERLAP_DISCOUNT: '회원 중복 할인',
  GRADE_MILEAGE: '회원 추가 마일리지',
  COUPON: '상품 쿠폰',
};
import { ArtistSelector } from './ArtistSelector';
import type { ProductServerFieldError } from '../lib/productServerFieldError';
import { BrandSelector } from './BrandSelector';
import { ProductCategoriesField } from './ProductCategoriesField';
import { ProductVisibleMallsField } from './ProductVisibleMallsField';
import { ArtistMembersSelector } from './ArtistMembersSelector';
import { SectionNav, type SectionNavItem } from './SectionNav';
import { SkuTable, type SkuFlatEntry, NoOptionStockFields } from './SkuTable';
import { visibleOptionLabel } from '@/lib/catalog/optionLabel';
import { OptionConfigBlock, type OptionMode } from './OptionConfigBlock';
import { useProductOptionGroupsQuery } from '../hooks/useProductOptionGroupsQuery';
import {
  comboVariantIdSet,
  partitionVariantEntries,
  resolveComboLabelPairs,
} from '../lib/variantPartition';
import { TranslationsTabs } from './TranslationsTabs';
import { OverseasShippingSummary } from './OverseasShippingSummary';
import { NoticeSpecEditor } from './NoticeSpecEditor';
import { ProductEventsField, type EventLinkDraft } from './ProductEventsField';
import { ProductCouponsField, type CouponLinkDraft } from './ProductCouponsField';
import { ProductMultiSelectModalField } from './ProductMultiSelectModalField';
import { ProductKeywordsField } from './ProductKeywordsField';
import {
  ProductCollectionMallTabs,
  emptyCollectionByMall,
  type ProductCollectionByMall,
} from '../productCollection/ProductCollectionMallTabs';
import { ProductCommonConsentsField } from '../commonConsents/components/ProductCommonConsentsField';
import type { CollectionMall } from '../productCollection/collectionMalls';
import type { ProductCollectionConfig } from '../productCollection/types';
import type { ProductAuditTimes } from '../lib/detailMapper';
import { formatAuditTime } from '../lib/auditTime';
import { AdminNameCell } from '@/features/admin/components/AdminNameCell';
import { MemberGradePicker } from './MemberGradePicker';
import { useExchangeRatesQuery } from '@/features/exchangeRates/hooks/useExchangeRatesQuery';
import type { ExchangeRateCurrency } from '@/features/exchangeRates/types';

// L-1: 그룹 멤버 선택 UI를 현재 미사용으로 숨김. 재노출 시 true.
const SHOW_ARTIST_MEMBERS = false;

/** 등록자·수정자 칸 — 옆의 등록일·수정일(읽기 전용 Input)과 같은 모양. */
const AUDIT_ADMIN_BOX = 'flex w-full items-center overflow-hidden rounded-md border px-3 text-[13px] opacity-50';
const AUDIT_ADMIN_BOX_STYLE = {
  height: 'var(--control-h-md)',
  background: 'var(--surface-0)',
  color: 'var(--content-strong)',
  borderColor: 'var(--divider-strong)',
} as const;

const SHIPPING_FEE_TYPE_OPTIONS: ReadonlyArray<{ value: ShippingFeeType; label: string }> = [
  { value: 'FREE', label: '무료 배송' },
  { value: 'FLAT', label: '정액 배송비' },
  { value: 'CONDITIONAL', label: '조건부 무료' },
  { value: 'REGION', label: '지역별 차등' },
];

const CURRENCY_LABELS: Record<keyof CurrencyPrices, string> = {
  KRW: '₩ KRW',
  USD: '$ USD',
  JPY: '¥ JPY',
  CNY: '¥ CNY',
};

const CURRENCY_STEP: Record<keyof CurrencyPrices, string> = {
  KRW: '1',
  USD: '0.01',
  JPY: '1',
  CNY: '0.01',
};

// KRW 는 항상 필수 기준 통화. 나머지는 선택적 오버라이드(비우면 환율 자동계산).
const OVERRIDE_CURRENCIES: ReadonlyArray<ExchangeRateCurrency> = ['USD', 'JPY', 'CNY'];

// 외화 표시 소수 자리 — JPY 는 정수, USD/CNY 는 소수 2자리로 미리보기 반올림.
const CURRENCY_FRACTION_DIGITS: Record<ExchangeRateCurrency, number> = {
  USD: 2,
  JPY: 0,
  CNY: 2,
};

/** KRW 기준가 ÷ 환율 → 외화 자동계산 미리보기 문자열. 환율 없거나 0 이하면 null. */
const autoConvert = (
  krwAmount: number | null | undefined,
  krwPerUnit: number | undefined,
  currency: ExchangeRateCurrency,
): string | null => {
  if (krwAmount == null || !Number.isFinite(krwAmount) || krwAmount <= 0) return null;
  if (krwPerUnit == null || !Number.isFinite(krwPerUnit) || krwPerUnit <= 0) return null;
  const converted = krwAmount / krwPerUnit;
  return converted.toLocaleString('ko-KR', {
    minimumFractionDigits: CURRENCY_FRACTION_DIGITS[currency],
    maximumFractionDigits: CURRENCY_FRACTION_DIGITS[currency],
  });
};

export interface ProductFormSubmit {
  values: ProductFormValues;
  eventLinks: EventLinkDraft[];
  couponLinks: CouponLinkDraft[];
  /** 이 상품에 적용할 PRODUCT scope 적립 정책 id. null = 기본 정책 사용(매핑 해제). */
  earnPolicyId: string | null;
  /** 항목2 — 상품 커스텀 폼 + 가변 동의 항목(4몰별 맵). 호스트가 몰별 PUT 한다. */
  collectionByMall: ProductCollectionByMall;
  /** QA-463/570 메인 페이지 섹션 노출(sectionKey → 노출). undefined = 섹션 상태를 몰라 체크를 숨김(호스트는 섹션을 건드리지 않는다). */
  homeSections?: ProductHomeSections | undefined;
}

export interface ProductFormProps {
  id?: string;
  defaultValues?: ProductFormValues;
  /** #10c 편집 모드일 때 조합 옵션 reload 대상 상품 UUID. 등록 모드에선 미전달. */
  editProductId?: string | null;
  defaultEventLinks?: ReadonlyArray<EventLinkDraft>;
  defaultCouponLinks?: ReadonlyArray<CouponLinkDraft>;
  defaultEarnPolicyId?: string | null;
  /** QA-463/570 메인 섹션 목록(상태를 아는 사용 중 섹션). */
  homeSectionOptions?: ReadonlyArray<ProductHomeSectionOption>;
  /** QA-463/570 섹션별로 이미 있는가. undefined = 아직 모름/섹션 없음(체크 숨김). */
  defaultHomeSections?: ProductHomeSections | undefined;
  /** 항목2 — 초기 커스텀 폼/동의 4몰 맵(편집 시 seed). 미지정 시 빈 4몰 구성. */
  defaultCollectionByMall?: ProductCollectionByMall;
  vendorId?: string | null;
  submitting?: boolean;
  onSubmit: (submit: ProductFormSubmit) => void;
  onCancel?: () => void;
  submitLabelKey?: string;
  /** undefined = 내장 footer 표시, false = footer 숨김, ReactNode = 커스텀 footer */
  footerSlot?: React.ReactNode | false;
  /** 편집 화면 기본 정보의 등록일/수정일(읽기 전용). 등록 화면에선 미전달. */
  auditTimes?: ProductAuditTimes;
  /** 저장이 BE 에서 거절된 원인 칸 — 그 칸으로 옮겨 가 칸 아래에 사유를 보인다(productServerFieldError). */
  serverFieldError?: ProductServerFieldError | null;
}

/** 저장 거절(아티스트) 시 스크롤 대상. */
const ARTIST_FIELD_ID = 'product-field-artist';

const TYPE_OPTIONS = ['GENERAL', 'PRESALE', 'EVENT', 'SET', 'CONCERT'] as const;
const STATUS_OPTIONS = ['ON_SALE', 'SALE_ENDED', 'HIDDEN', 'PREPARING'] as const;

interface SectionCardProps {
  id: string;
  index: number;
  title: string;
  description?: string;
  children: React.ReactNode;
}

const SectionCard = ({ id, index, title, description, children }: SectionCardProps) => (
  <section
    id={id}
    className="scroll-mt-24 rounded-lg p-6"
    style={{
      background: 'var(--surface-raised)',
      border: '1px solid var(--divider-strong)',
    }}
  >
    <header className="mb-5 flex items-baseline justify-between gap-3 pb-4" style={{ borderBottom: '1px solid var(--divider-soft)' }}>
      <div className="flex items-baseline gap-2.5">
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold tabular-nums"
          style={{
            background: 'var(--cta-primary)',
            color: 'var(--cta-primary-on)',
          }}
        >
          {index}
        </span>
        <h3
          className="text-[18px] font-bold"
          style={{
            fontFamily: 'var(--font-display)',
            color: 'var(--content-strong)',
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h3>
      </div>
      {description ? (
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {description}
        </p>
      ) : null}
    </header>
    {children}
  </section>
);

interface FieldRowProps {
  label: string;
  required?: boolean;
  hint?: string;
  /** 'horizontal' (default): 좌측 라벨 + 우측 input. 'vertical': 라벨 위 + input 아래 (1x2 grid 안에서 사용). */
  layout?: 'horizontal' | 'vertical';
  children: React.ReactNode;
}

const FieldRow = ({ label, required, hint, layout = 'horizontal', children }: FieldRowProps) => {
  if (layout === 'vertical') {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <label className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
          {label}
          {required ? <RequiredMark /> : null}
        </label>
        <div className="min-w-0">{children}</div>
        {hint ? (
          <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[200px_1fr] md:gap-4">
      <div className="pt-2">
        <label className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
          {label}
          {required ? <RequiredMark /> : null}
        </label>
        {hint ? (
          <p className="mt-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {hint}
          </p>
        ) : null}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
};

/** 1x2 / 1x3 grid wrapper. children은 FieldRow(layout='vertical') 또는 자유. */
const FieldGrid = ({ cols = 2, children }: { cols?: 2 | 3; children: React.ReactNode }) => (
  <div
    className="grid grid-cols-1 gap-5 md:gap-x-6"
    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
  >
    {children}
  </div>
);

export const ProductForm = ({
  id,
  defaultValues,
  editProductId = null,
  defaultEventLinks = [],
  defaultCouponLinks = [],
  defaultEarnPolicyId = null,
  homeSectionOptions = [],
  defaultHomeSections,
  defaultCollectionByMall,
  vendorId = null,
  submitting = false,
  onSubmit,
  onCancel,
  submitLabelKey = 'products.actions.save',
  footerSlot,
  auditTimes,
  serverFieldError = null,
}: ProductFormProps) => {
  const { t } = useTranslation('products');
  // #10c 편집 모드 = defaultValues 제공(수정). 조합 옵션 편집기는 등록에서만, 수정은 조회.
  const isEditMode = defaultValues != null;
  // 저장 거절 원인이 아티스트면 그 칸으로 옮겨 가 칸 아래에 사유를 보인다. 아티스트를 바꾸면 사유를 지운다.
  const [artistServerError, setArtistServerError] = useState<string | null>(null);
  useEffect(() => {
    if (serverFieldError?.field !== 'artist') {
      setArtistServerError(null);
      return;
    }
    setArtistServerError(serverFieldError.message);
    const el = document.getElementById(ARTIST_FIELD_ID);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true });
  }, [serverFieldError]);
  // V131·V132 구매·접근 권한 라디오(구매/접근 공용).
  const memberPermissionOptions = [
    { value: 'ALL', label: t('products.fields.permissionAll', '전체(회원+비회원)') },
    { value: 'MEMBER', label: t('products.fields.permissionMember', '회원전용') },
    { value: 'GRADE', label: t('products.fields.permissionGrade', '특정회원등급') },
  ];
  // 구매 쪽은 고도몰 문구대로 "회원전용(비회원 제외)".
  const purchasePermissionOptions = memberPermissionOptions.map((o) =>
    o.value === 'MEMBER' ? { ...o, label: t('products.fields.permissionMemberNoGuest', '회원전용(비회원 제외)') } : o,
  );
  // 편집 모드에서만 조합 옵션 reload(읽기 전용 표시). 등록 모드/조합 미사용 상품은 빈 결과.
  const optionGroupsQuery = useProductOptionGroupsQuery(isEditMode ? editProductId : null);
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: defaultValues ?? emptyProductFormValues(),
    mode: 'onBlur',
  });

  const variants = useFieldArray({ control: form.control, name: 'variants' });
  // §4 조합/일반 분리 — 조합-생성 variant(option-groups 응답 variantCombinations[].variantId)는
  // 그룹 섹션의 "조합 목록"에서 재고 채널만 편집하고, 일반(평면) 행 목록에는 비조합 variant 만
  // 남긴다. 분류는 **렌더 전용** — 페이로드는 전 행(조합+일반)을 종전과 동일하게 전송한다
  // (detailMapper buildUpdateVariants 무변경, d570d33 stock-only patch 계약).
  const comboVariantIds = comboVariantIdSet(optionGroupsQuery.data?.variantCombinations);
  const variantEntries = partitionVariantEntries(variants.fields, comboVariantIds);
  // 그룹-한정 라벨 — 다중 그룹에서 선택지 라벨만으로는 행이 모호("차콜"이 어느 그룹 값인지).
  // variantCombinations 의 (groupId, choiceId) 링크를 그룹/선택지 정의로 해석해
  // "색상: 차콜 · 사이즈: S" 쌍을 만들고, 해석 불가 조합은 null = 원본 라벨 폴백.
  const comboPairsByVariantId = new Map(
    (optionGroupsQuery.data?.variantCombinations ?? []).map((combination) => [
      combination.variantId,
      resolveComboLabelPairs(combination, optionGroupsQuery.data?.optionGroups ?? []),
    ]),
  );
  const comboEntries = variantEntries.combo.map(({ field, index }) => ({
    fieldId: field.id,
    index,
    label: field.label,
    labelPairs:
      field.variantId != null ? (comboPairsByVariantId.get(field.variantId) ?? null) : null,
    variantCode: field.variantCode ?? null,
    additionalAmount: field.additionalAmount ?? null,
  }));
  // 평면(비조합) variant 행 — SkuTable 의 편집 가능 행. 인덱스 보존(RHF 경로).
  const flatEntries: ReadonlyArray<SkuFlatEntry> = variantEntries.flat.map(({ field, index }) => ({
    fieldId: field.id,
    index,
  }));
  // 혼용 금지 — 옵션 그룹이 활성(편집기에 1개 이상)이면 일반 옵션 행 추가를 차단한다
  // (그룹 모드에서 BE 는 평면 구조 입력을 무시). 기존 비조합 행은 그대로 표시·편집 가능
  // (레거시 하이브리드 상품 보존).
  const optionGroupsActive = (form.watch('optionGroups') ?? []).length > 0;
  // G2 — 옵션 방식(단일/조합) UI 파생 상태. 스키마 신규 필드 없음. 조합 그룹/조합 variant 가 있으면
  // 조합 모드로 시작한다(편집 로드 시 조합 상품은 조합 모드). 사용자가 토글로 바꿀 수 있다.
  // QA-521 — 평면 옵션이 0~1개이고 라벨이 비었거나 기본("기본")이면 '옵션이 없는 상품'으로 시작한다.
  const [optionMode, setOptionMode] = useState<OptionMode>(() => {
    if (optionGroupsActive || comboEntries.length > 0) return 'combo';
    const first = flatEntries[0];
    const firstLabel = first ? (form.getValues(`variants.${first.index}.label`) ?? '') : '';
    return flatEntries.length <= 1 && visibleOptionLabel(firstLabel) == null ? 'none' : 'single';
  });
  const [optionModeBlocked, setOptionModeBlocked] = useState<string | null>(null);
  const changeOptionMode = (next: OptionMode) => {
    if (next === 'none') {
      if (optionGroupsActive || comboEntries.length > 0 || flatEntries.length > 1) {
        setOptionModeBlocked(
          t(
            'products.optionGroups.noneBlocked',
            '옵션이 2개 이상이거나 조합 옵션이 있어 바꿀 수 없습니다. 옵션을 1개만 남긴 뒤 다시 골라 주세요.',
          ),
        );
        return;
      }
      // 남은 1개 옵션은 기본 SKU 가 된다 — 라벨을 비워 저장 때 "기본"으로.
      const first = flatEntries[0];
      if (first) form.setValue(`variants.${first.index}.label`, '', { shouldDirty: true });
    }
    setOptionModeBlocked(null);
    setOptionMode(next);
  };
  // 옵션이 없는 상품은 기본 SKU 1행에 재고를 입력한다 — 행이 없으면(신규 등록) 하나 만든다.
  const appendVariant = variants.append;
  useEffect(() => {
    if (optionMode === 'none' && flatEntries.length === 0 && !optionGroupsActive && comboEntries.length === 0) {
      // QA-585 — 새 옵션은 무한판매(재고추적 끔)로 시작. 재고를 관리하려면 체크를 켠다.
      appendVariant({ label: '', initialStockQty: 0, stockTrackingEnabled: false });
    }
  }, [optionMode, flatEntries.length, optionGroupsActive, comboEntries.length, appendVariant]);
  // 편집 로드(시드)/그룹 활성화 시 조합 모드로 승격 — 데이터가 조합인데 단일 모드로 갇히지 않게.
  // 'single' 로 직접 전환하는 사용자 동작은 덮어쓰지 않는다(승격만, 강등 없음).
  useEffect(() => {
    if (optionGroupsActive || comboEntries.length > 0) {
      setOptionMode((prev) => (prev === 'combo' ? prev : 'combo'));
    }
  }, [optionGroupsActive, comboEntries.length]);
  const productType = form.watch('productType');
  const showEventSubtitle = productType === 'EVENT';
  const shippingFeeType = form.watch('shippingFeeType') ?? 'FREE';

  // M-8 ID81 — 신규 상품 등록 시 배송비 기본 템플릿 자동선택(GET /fee-policies/default).
  // 편집(id 존재)은 기존 값을 보존하므로 프리필하지 않는다. 사용자가 이미 값을 골랐으면
  // 덮어쓰지 않는다(미선택일 때만 1회 기본 템플릿으로 시드).
  const isCreate = id == null;
  const defaultShippingFee = useDefaultShippingFeePolicyQuery(isCreate);
  useEffect(() => {
    if (!isCreate) return;
    const defaultId = defaultShippingFee.data?.policyId;
    if (!defaultId) return;
    if (form.getValues('shippingFeePolicyId') == null) {
      form.setValue('shippingFeePolicyId', defaultId, { shouldDirty: false });
    }
  }, [isCreate, defaultShippingFee.data, form]);

  // 외화 자동계산 미리보기용 — 활성 환율 맵 + KRW 기준가 watch.
  const exchangeRatesQuery = useExchangeRatesQuery();
  const krwPerUnitByCurrency = new Map<ExchangeRateCurrency, number>(
    (exchangeRatesQuery.data ?? [])
      .filter((rate) => rate.active)
      .map((rate) => [rate.currencyCode, rate.krwPerUnit]),
  );
  const krwListAmount = form.watch('prices.KRW.listAmount');
  const krwSaleAmount = form.watch('prices.KRW.saleAmount');

  const [eventLinks, setEventLinks] = useState<EventLinkDraft[]>([...defaultEventLinks]);
  const [couponLinks, setCouponLinks] = useState<CouponLinkDraft[]>([...defaultCouponLinks]);
  const [earnPolicyId, setEarnPolicyId] = useState<string | null>(defaultEarnPolicyId);
  // QA-463/570 — 섹션 상태를 늦게 알게 되면(등록 화면) 그때 채운다. 운영자가 이미 바꾼 섹션은 덮지 않는다.
  const [homeSections, setHomeSections] = useState<ProductHomeSections | undefined>(defaultHomeSections);
  useEffect(() => {
    if (defaultHomeSections !== undefined) setHomeSections((prev) => ({ ...defaultHomeSections, ...(prev ?? {}) }));
  }, [defaultHomeSections]);
  const [collectionByMall, setCollectionByMall] = useState<ProductCollectionByMall>(
    defaultCollectionByMall ?? emptyCollectionByMall(),
  );
  const updateCollectionMall = (mall: CollectionMall, next: ProductCollectionConfig) =>
    setCollectionByMall((prev) => ({ ...prev, [mall]: next }));
  // ID39 노출 예약 토글 — 편집 진입 시 시작/종료 중 하나라도 있으면 켜진 상태로 시드.
  const [displayScheduleOn, setDisplayScheduleOn] = useState<boolean>(
    Boolean(defaultValues?.displayStartAt || defaultValues?.displayEndAt),
  );

  const uploads = useImageUploadQueue();

  // 결정④ — 편집 모드 조합 옵션 편집: GET option-groups 결과를 폼 optionGroups 로 1회 시드.
  // groupId/choiceId 를 보존해 BE reconcile 의 ID-우선 매칭 키로 쓴다(rename-safe — 이름/라벨을
  // 바꿔도 기존 variant 재고/SKU 생존). 시드는 폼 값이 비어 있을 때만(사용자 편집 덮어쓰기 방지).
  // id 는 save 후 재생성되므로 update mutation 이 option-groups 캐시를 무효화한다(재시드 계약).
  const [optionGroupsSeeded, setOptionGroupsSeeded] = useState(false);
  useEffect(() => {
    if (!isEditMode || optionGroupsSeeded) return;
    const data = optionGroupsQuery.data;
    if (!data) return;
    if (data.optionGroups.length > 0 && (form.getValues('optionGroups') ?? []).length === 0) {
      form.setValue(
        'optionGroups',
        data.optionGroups.map((g) => ({
          groupId: g.groupId,
          name: g.name,
          required: g.required,
          position: g.position,
          choices: g.choices.map((c) => ({
            choiceId: c.choiceId,
            label: c.label,
            additionalAmount: c.additionalAmount,
            position: c.position,
          })),
        })),
        { shouldDirty: false },
      );
    }
    setOptionGroupsSeeded(true);
  }, [isEditMode, optionGroupsSeeded, optionGroupsQuery.data, form]);

  const handleSubmit = async (values: ProductFormValues) => {
    let resolved: Awaited<ReturnType<typeof uploads.flush>>;
    try {
      resolved = await uploads.flush();
    } catch {
      return;
    }
    const thumbnail = resolved.thumbnailUrl;
    const detail = resolved.detailImages;
    const merged: ProductFormValues = {
      ...values,
      thumbnailUrl: typeof thumbnail === 'string' ? thumbnail : null,
      detailImages: Array.isArray(detail) ? detail : [],
    };
    onSubmit({ values: merged, eventLinks, couponLinks, earnPolicyId, collectionByMall, homeSections });
  };

  /**
   * 검증에 실패하면 **항상** 알린다. 입력칸이 있는 필드는 그 아래에 오류가 뜨고 RHF 가 그 칸으로
   * 포커스를 옮기지만, 화면에 입력칸이 없는 필드에서 실패하면 아무것도 보이지 않아 저장 버튼이
   * 반응하지 않는 것처럼 된다 — 이관 상품이 `listPrice`(가격 UI 는 prices.KRW 를 바인딩) 에서 그랬다.
   * 첫 오류의 문구와 경로를 보여줘 운영자가 무엇을 고칠지(또는 무엇을 제보할지) 알 수 있게 한다.
   */
  const handleInvalid = (errors: FieldErrors<ProductFormValues>) => {
    const found = collectFormErrors(errors);
    const first = found[0];
    // 검색 키워드가 너무 길면 "(searchKeywords.0)" 대신 어떤 키워드인지·어디서 고치는지 알려 주고 그 섹션으로 옮긴다.
    const kwMatch = first?.path.match(/^searchKeywords\.(\d+)$/);
    if (kwMatch) {
      const keyword = (form.getValues('searchKeywords') ?? [])[Number(kwMatch[1])] ?? '';
      toast.error(
        t('products.form.searchKeywordTooLong', {
          keyword,
          length: keyword.length,
          max: SEARCH_KEYWORD_MAX,
          defaultValue:
            '저장할 수 없습니다 — 검색 키워드 "{{keyword}}" 가 {{max}}자를 넘습니다({{length}}자). [검색 · 노출 최적화]에서 줄이거나 지워 주세요.',
        }),
      );
      document.getElementById('section-seo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    toast.error(
      first
        ? t('products.form.submitInvalid', {
            reason: t(first.message, first.message),
            field: first.path,
            count: found.length,
            defaultValue: '저장할 수 없습니다 — {{reason}} ({{field}})',
          })
        : t('products.form.submitInvalidUnknown', { defaultValue: '저장할 수 없습니다 — 입력값을 확인해 주세요.' }),
    );
  };

  const navItems: ReadonlyArray<SectionNavItem> = [
    { id: 'section-basic', label: t('products.sections.basic', '상품 기본정보') },
    { id: 'section-translations', label: t('products.sections.translations', '다국어 명칭/설명/상세') },
    { id: 'section-pricing', label: t('products.sections.pricing', '판매 가격') },
    { id: 'section-variants', label: t('products.sections.variants', '옵션 / 재고') },
    { id: 'section-detail', label: t('products.sections.detailImages', '상세 페이지 이미지') },
    { id: 'section-schedule', label: t('products.sections.schedule', '판매 일정') },
    { id: 'section-shipping', label: t('products.sections.shipping', '배송') },
    { id: 'section-policy', label: t('products.sections.policy', '정책 연결') },
    { id: 'section-seo', label: t('products.sections.seo', '검색 · 노출 최적화') },
    { id: 'section-events', label: t('products.sections.events', '이벤트 연결') },
    { id: 'section-notice', label: t('products.sections.noticeSpec', '상품 정보 고시') },
    { id: 'section-collection', label: t('products.sections.collection', '추가 입력 · 동의') },
  ];

  return (
    <Form {...form}>
      <form
        id={id}
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        aria-label={t('products.form.label')}
        noValidate
        data-testid="product-create-form"
        className="grid gap-8"
        style={{ gridTemplateColumns: 'minmax(0, 220px) minmax(0, 1fr)' }}
      >
        <SectionNav items={navItems} topOffset={96} />

        <div className="flex min-w-0 flex-col gap-6">
          {uploads.error ? (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-lg px-4 py-3 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
                border: '1px solid var(--divider-strong)',
              }}
            >
              {uploads.error}
            </div>
          ) : null}

          {/* 1. 상품 기본정보 */}
          <SectionCard id="section-basic" index={1} title={t('products.sections.basic', '상품 기본정보')}>
            <div className="flex flex-col gap-5">
              <FieldRow label={t('products.fields.nameKo', '상품명 (한국어)')} required>
                <FormField
                  control={form.control}
                  name="nameKo"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} required aria-required="true" placeholder={t('products.fields.nameKoPlaceholder', '대표 상품명 (한국어)')} />
                      </FormControl>
                      <FormMessage>
                        {form.formState.errors.nameKo?.message
                          ? t(form.formState.errors.nameKo.message, { defaultValue: form.formState.errors.nameKo.message })
                          : null}
                      </FormMessage>
                    </FormItem>
                  )}
                />
              </FieldRow>

              <FieldRow label={t('products.fields.productType', '상품 유형')} required>
                <FormField
                  control={form.control}
                  name="productType"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          orientation="horizontal"
                          aria-label={t('products.fields.productType', '상품 유형')}
                          options={TYPE_OPTIONS.map((opt) => ({
                            value: opt,
                            label: t(`products.types.${opt}`),
                          }))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FieldRow>

              <FieldRow
                label={t('products.fields.status', '상품 상태')}
                required
                hint={t('products.fields.statusHint', '판매 상태 (노출여부와 별개)')}
              >
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={(v) => {
                            // QA-606 — [비공개]는 노출 끔: 노출 여부 체크도 같이 끈다. 비공개에서 다른 상태로 바꾸면 다시 켠다.
                            if (v === 'HIDDEN') form.setValue('displayed', false, { shouldDirty: true });
                            else if (field.value === 'HIDDEN') form.setValue('displayed', true, { shouldDirty: true });
                            field.onChange(v);
                          }}
                          orientation="horizontal"
                          aria-label={t('products.fields.status', '상품 상태')}
                          options={STATUS_OPTIONS.map((opt) => ({
                            value: opt,
                            label: t(`products.status.${opt}`),
                          }))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {/* V130 수동 품절(고도몰 품절 표시) — 수정 화면에서 BE 가 값을 내려줄 때만 보인다(등록 API 미지원). */}
                <Controller
                  control={form.control}
                  name="manualSoldOut"
                  render={({ field }) =>
                    field.value === undefined ? <></> : (
                      <div className="mt-3 flex flex-col gap-1" data-testid="manual-sold-out">
                        <label
                          className="inline-flex items-center gap-2 text-[14px]"
                          style={{ color: 'var(--content-default)' }}
                        >
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            aria-label={t('products.fields.manualSoldOut', '수동 품절')}
                          />
                          {t('products.fields.manualSoldOut', '수동 품절')}
                        </label>
                        <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                          {t(
                            'products.fields.manualSoldOutHint',
                            '체크 시 재고와 관계없이 구매할 수 없습니다. 재고를 입력해도 자동으로 판매가 재개되지 않습니다. 판매를 재개하려면 체크를 해제하세요.',
                          )}
                        </p>
                        {field.value && form.watch('status') === 'ON_SALE' ? (
                          <p className="text-[12px]" style={{ color: 'var(--status-warn-fg)' }} data-testid="manual-sold-out-warning">
                            {t(
                              'products.fields.manualSoldOutOnSaleWarning',
                              '수동 품절이 체크되어 있어 "판매중"으로 저장해도 품절로 유지됩니다.',
                            )}
                          </p>
                        ) : null}
                      </div>
                    )
                  }
                />
              </FieldRow>

              <FieldRow
                label={t('products.fields.displayed', '노출 여부')}
                hint={t('products.fields.displayedHint', '끔 = 스토어프론트에 숨김 (판매 상태와 무관하게 전시 차단)')}
              >
                <div className="flex flex-col gap-3">
                  <Controller
                    control={form.control}
                    name="displayed"
                    render={({ field }) => (
                      <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                        <input
                          type="checkbox"
                          checked={field.value ?? true}
                          onChange={(e) => field.onChange(e.target.checked)}
                          aria-label={t('products.fields.displayed', '노출 여부')}
                        />
                        {field.value ?? true
                          ? t('products.fields.displayedOn', '전시 중 (노출)')
                          : t('products.fields.displayedOff', '숨김 (미노출)')}
                      </label>
                    )}
                  />

                  {/* ID39 노출 예약 — 활성화 시 시작/종료 데이트피커. 종료 빈값=상시노출. */}
                  <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                    <input
                      type="checkbox"
                      checked={displayScheduleOn}
                      onChange={(e) => {
                        setDisplayScheduleOn(e.target.checked);
                        if (!e.target.checked) {
                          // 예약 해제 시 window 값 비움(상시 displayed 토글로 복귀).
                          form.setValue('displayStartAt', null, { shouldDirty: true });
                          form.setValue('displayEndAt', null, { shouldDirty: true });
                        }
                      }}
                      aria-label={t('products.fields.displaySchedule', '노출 예약')}
                    />
                    {t('products.fields.displaySchedule', '노출 예약')}
                  </label>

                  {displayScheduleOn ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <FieldRow label={t('products.fields.displayStartAt', '노출 시작일')} layout="vertical">
                        <FormField
                          control={form.control}
                          name="displayStartAt"
                          render={({ field }) => (
                            <Input type="datetime-local" {...field} value={field.value ?? ''} aria-label={t('products.fields.displayStartAt', '노출 시작일')} />
                          )}
                        />
                      </FieldRow>
                      <FieldRow
                        label={t('products.fields.displayEndAt', '노출 종료일')}
                        hint={t('products.fields.displayEndAtHint', '비우면 상시 노출 (수동 숨김 전까지)')}
                        layout="vertical"
                      >
                        <FormField
                          control={form.control}
                          name="displayEndAt"
                          render={({ field }) => (
                            <Input type="datetime-local" {...field} value={field.value ?? ''} aria-label={t('products.fields.displayEndAt', '노출 종료일')} />
                          )}
                        />
                      </FieldRow>
                    </div>
                  ) : null}
                </div>
              </FieldRow>

              {/* F-1: B2B 상품 전용. true=B2B(사업자) 리스트에서만 노출, 일반 PLP/검색 제외. */}
              <FieldRow
                label={t('products.fields.b2bOnly', 'B2B 상품 전용')}
                hint={t('products.fields.b2bOnlyHint', '켬 = 사업자(B2B) 전용 상품. 일반 고객 목록/검색에서 숨기고 B2B 화면에서만 노출됩니다.')}
              >
                <Controller
                  control={form.control}
                  name="b2bOnly"
                  render={({ field }) => (
                    <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                      <input
                        type="checkbox"
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        aria-label={t('products.fields.b2bOnly', 'B2B 상품 전용')}
                      />
                      {field.value ?? false
                        ? t('products.fields.b2bOnlyOn', 'B2B 전용 (사업자만 노출)')
                        : t('products.fields.b2bOnlyOff', '일반 상품 (전체 노출)')}
                    </label>
                  )}
                />
              </FieldRow>

              {/* QA-463/570 — 메인 페이지 섹션(NEW RELEASES·EVER MUSIC·EVER MD …) 노출을 상품 등록·수정에서 바로(고도몰처럼). 저장 후 섹션에 넣고 뺀다. */}
              {homeSections !== undefined && homeSectionOptions.length > 0 ? (
                <FieldRow
                  label={t('products.fields.homeSections', '메인 섹션 노출')}
                  hint={t('products.fields.homeSectionsHint', '체크한 메인 섹션 맨 앞에 넣습니다. 순서는 메인 페이지 관리에서 바꿉니다.')}
                >
                  <div className="flex flex-col gap-1.5" data-testid="product-home-sections">
                    {homeSectionOptions.map((s) => (
                      <label
                        key={s.sectionKey}
                        className="inline-flex items-center gap-2 text-[14px]"
                        style={{ color: 'var(--content-default)' }}
                      >
                        <input
                          type="checkbox"
                          checked={homeSections[s.sectionKey] ?? false}
                          onChange={(e) => setHomeSections((prev) => ({ ...(prev ?? {}), [s.sectionKey]: e.target.checked }))}
                          data-testid={`product-home-section-${s.sectionKey}`}
                        />
                        {s.label} ({s.sectionKey})
                        {s.auto ? (
                          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                            — 자동 진열 중(최신 등록순)이라 메인 페이지 관리에서 수동으로 바꿔야 반영됩니다.
                          </span>
                        ) : null}
                      </label>
                    ))}
                  </div>
                </FieldRow>
              ) : null}

              {/* V131 구매 가능 회원(고도몰 goodsPermission). 회원전용이면 비회원 주문 불가 + 가격 대체 문구(선택). */}
              <FieldRow
                label={t('products.fields.purchasePermission', '구매 가능 회원')}
                hint={t('products.fields.purchasePermissionHint', '회원전용이면 비회원은, 특정회원등급이면 고른 등급 외의 고객은 주문할 수 없습니다.')}
              >
                <div className="flex flex-col gap-3" data-testid="purchase-permission">
                  <Controller
                    control={form.control}
                    name="purchasePermission"
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? 'ALL'}
                        onValueChange={field.onChange}
                        orientation="horizontal"
                        aria-label={t('products.fields.purchasePermission', '구매 가능 회원')}
                        options={purchasePermissionOptions}
                      />
                    )}
                  />
                  {form.watch('purchasePermission') === 'GRADE' ? (
                    <MemberGradePicker
                      control={form.control}
                      name="purchaseGradeIds"
                      includeGuest
                      label={t('products.fields.purchaseGrades', '구매 가능 등급')}
                      testId="purchase-grade-ids"
                    />
                  ) : null}
                  {/* 고도몰 goodsPermissionPriceStringFl + 문구. 에버라인은 "문구 있음 = 사용" 하나로 저장하므로
                      체크를 풀면 "" 를 보내 문구가 지워진다(detailMapper). */}
                  {form.watch('purchasePermission') !== 'ALL' && form.watch('purchasePermission') !== undefined ? (
                    <div className="flex flex-col gap-2">
                      <Controller
                        control={form.control}
                        name="purchaseDeniedTextEnabled"
                        render={({ field }) => (
                          <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                            <input
                              type="checkbox"
                              checked={field.value ?? false}
                              onChange={(e) => field.onChange(e.target.checked)}
                              aria-label={t('products.fields.purchaseDeniedTextEnabled', '구매 불가 고객 가격 대체 문구 사용')}
                            />
                            {t('products.fields.purchaseDeniedTextEnabled', '구매 불가 고객 가격 대체 문구 사용')}
                          </label>
                        )}
                      />
                      {form.watch('purchaseDeniedTextEnabled') ? (
                    <FieldRow
                      label={t('products.fields.purchaseDeniedText', '구매 불가 고객 가격 대체 문구')}
                      hint={t('products.fields.purchaseDeniedTextHint', '구매할 수 없는 고객에게 가격 대신 보입니다. (최대 100자)')}
                      layout="vertical"
                    >
                      <FormField
                        control={form.control}
                        name="purchaseDeniedText"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value ?? ''}
                                maxLength={100}
                                placeholder={t('products.fields.purchaseDeniedTextPlaceholder', '예: 로그인 후 구매 가능')}
                                aria-label={t('products.fields.purchaseDeniedText', '구매 불가 고객 가격 대체 문구')}
                              />
                            </FormControl>
                            <FormMessage>
                              {fieldState.error?.message
                                ? t(fieldState.error.message, { defaultValue: fieldState.error.message })
                                : null}
                            </FormMessage>
                          </FormItem>
                        )}
                      />
                    </FieldRow>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </FieldRow>

              {/* V131 접근 권한(고도몰 goodsAccess). 회원전용이면 비회원은 상세 접근 불가, 목록 노출은 체크박스로. */}
              <FieldRow
                label={t('products.fields.accessPermission', '접근 권한')}
                hint={t('products.fields.accessPermissionHint', '회원전용이면 비회원은, 특정회원등급이면 고른 등급 외의 고객은 상품 상세에 들어갈 수 없습니다.')}
              >
                <div className="flex flex-col gap-3" data-testid="access-permission">
                  <Controller
                    control={form.control}
                    name="accessPermission"
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? 'ALL'}
                        onValueChange={field.onChange}
                        orientation="horizontal"
                        aria-label={t('products.fields.accessPermission', '접근 권한')}
                        options={memberPermissionOptions}
                      />
                    )}
                  />
                  {form.watch('accessPermission') === 'GRADE' ? (
                    <MemberGradePicker
                      control={form.control}
                      name="accessGradeIds"
                      includeGuest
                      label={t('products.fields.accessGrades', '접근 가능 등급')}
                      testId="access-grade-ids"
                    />
                  ) : null}
                  {form.watch('accessPermission') !== 'ALL' && form.watch('accessPermission') !== undefined ? (
                    <Controller
                      control={form.control}
                      name="accessDeniedDisplay"
                      render={({ field }) => (
                        <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                          <input
                            type="checkbox"
                            checked={field.value ?? true}
                            onChange={(e) => field.onChange(e.target.checked)}
                            aria-label={t('products.fields.accessDeniedDisplay', '접근 불가 고객에게 상품 노출')}
                          />
                          {t('products.fields.accessDeniedDisplay', '접근 불가 고객에게 상품 노출')}
                        </label>
                      )}
                    />
                  ) : null}
                </div>
              </FieldRow>

              {/* QA-579 마일리지 사용 제외 — 결제 단계에서 고른 대상은 보유 마일리지를 이 상품에 쓸 수 없다(FE 얼럿, BE 결제 검사).
                  수정 화면에서 BE 가 필드를 아직 주지 않으면(undefined) 안내만 보이고 저장 때 보내지 않는다. */}
              <FieldRow
                label={t('products.fields.mileageUseExclusion', '마일리지 사용 제외')}
                hint={t(
                  'products.fields.mileageUseExclusionHint',
                  '고른 대상은 결제할 때 이 상품에 마일리지를 쓸 수 없습니다("해당 상품은 마일리지 사용이 불가합니다."). 비회원은 원래 마일리지를 쓸 수 없습니다.',
                )}
              >
                {form.watch('mileageUseExclusionTarget') === undefined ? (
                  <p className="text-[13px]" style={{ color: 'var(--content-muted)' }} data-testid="mileage-use-exclusion-not-ready">
                    {t('products.fields.mileageUseExclusionNotReady', '마일리지 사용 제외는 서버 반영 후 설정할 수 있습니다.')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-3" data-testid="mileage-use-exclusion">
                    <Controller
                      control={form.control}
                      name="mileageUseExclusionTarget"
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value ?? 'NONE'}
                          onValueChange={field.onChange}
                          orientation="horizontal"
                          aria-label={t('products.fields.mileageUseExclusion', '마일리지 사용 제외')}
                          options={[
                            { value: 'NONE', label: t('products.fields.mileageUseExclusionNone', '제외 안 함') },
                            { value: 'ALL', label: t('products.fields.mileageUseExclusionAll', '전체 회원') },
                            { value: 'GRADE', label: t('products.fields.mileageUseExclusionGrade', '특정회원등급') },
                          ]}
                        />
                      )}
                    />
                    {form.watch('mileageUseExclusionTarget') === 'GRADE' ? (
                      <MemberGradePicker
                        control={form.control}
                        name="mileageUseExclusionGradeIds"
                        label={t('products.fields.mileageUseExclusionGrades', '마일리지 사용 제외 등급')}
                        testId="mileage-use-exclusion-grade-ids"
                      />
                    ) : null}
                  </div>
                )}
              </FieldRow>

              {/* QA-456 혜택 제외(고도몰 [혜택 제외]) — 고른 혜택을 이 상품에서 뺀다. 대상: 전체회원 / 특정회원등급. */}
              <FieldRow
                label={t('products.fields.benefitExclusions', '혜택 제외')}
                hint={t(
                  'products.fields.benefitExclusionsHint',
                  '체크한 혜택은 고른 대상 회원에게 이 상품에서 적용되지 않습니다.',
                )}
              >
                <div className="flex flex-col gap-3" data-testid="benefit-exclusions">
                  <Controller
                    control={form.control}
                    name="benefitExclusions"
                    render={({ field }) => {
                      const selected = new Set(field.value ?? []);
                      return (
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {BENEFIT_EXCLUSIONS.map((v) => (
                            <label key={v} className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                              <input
                                type="checkbox"
                                checked={selected.has(v)}
                                onChange={(e) => {
                                  const next = new Set(selected);
                                  if (e.target.checked) next.add(v);
                                  else next.delete(v);
                                  field.onChange(BENEFIT_EXCLUSIONS.filter((x) => next.has(x)));
                                }}
                                data-testid={`benefit-exclusion-${v}`}
                              />
                              {t(`products.fields.benefitExclusion.${v}`, BENEFIT_EXCLUSION_LABELS[v])}
                            </label>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Controller
                    control={form.control}
                    name="benefitExclusionTarget"
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? 'ALL'}
                        onValueChange={field.onChange}
                        orientation="horizontal"
                        aria-label={t('products.fields.benefitExclusionTarget', '혜택 제외 대상')}
                        options={[
                          { value: 'ALL', label: t('products.fields.benefitExclusionTargetAll', '전체회원') },
                          { value: 'GRADE', label: t('products.fields.benefitExclusionTargetGrade', '특정회원등급') },
                        ]}
                      />
                    )}
                  />
                  {form.watch('benefitExclusionTarget') === 'GRADE' ? (
                    <MemberGradePicker
                      control={form.control}
                      name="benefitExclusionGradeIds"
                      includeGuest
                      label={t('products.fields.benefitExclusionGrades', '혜택 제외 등급')}
                      testId="benefit-exclusion-grade-ids"
                    />
                  ) : null}
                </div>
              </FieldRow>

              {/* mall-visibility 파트1 — 노출 국가(몰) 다중선택. 기본=전체노출(4몰).
                  일부만 선택 시 그 몰에만 노출(SF/BE 가 요청 몰로 분기). 비노출 PDP 진입은 SF redirect. */}
              <FieldRow
                label={t('products.fields.visibleMalls.label', '노출 국가(몰)')}
                hint={t('products.fields.visibleMalls.hint', '선택한 국가몰에만 노출됩니다. 기본은 전체 노출이며, 일부만 선택하면 나머지 몰에서는 숨겨집니다.')}
              >
                <Controller
                  control={form.control}
                  name="visibleMalls"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1">
                      <ProductVisibleMallsField
                        value={field.value ?? [...PRODUCT_LOCALES]}
                        disabled={submitting}
                        onChange={(next) => field.onChange(next)}
                      />
                      {fieldState.error?.message ? (
                        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
                          {t(fieldState.error.message, { defaultValue: fieldState.error.message })}
                        </span>
                      ) : null}
                    </div>
                  )}
                />
              </FieldRow>

              {/* QA-166 — 다중 카테고리 + 대표 카테고리. primaryCategoryId(=대표)와
                  additionalCategoryIds[] 를 함께 관리한다(둘 다 기존 스키마/매퍼 라운드트립).
                  대표는 라디오로 변경, [+] 로 다중 추가. 상위 카테고리 노출은 SF/BE가 조상 매칭으로 처리. */}
              <FieldRow
                label={t('products.fields.primaryCategoryId', '기본 카테고리')}
                hint={t('products.fields.categoriesHint', '여러 카테고리에 등록할 수 있습니다. 대표 카테고리는 라디오로 지정하세요.')}
                required
              >
                <Controller
                  control={form.control}
                  name="primaryCategoryId"
                  render={({ field, fieldState }) => (
                    <ProductCategoriesField
                      primaryCategoryId={field.value ?? null}
                      additionalCategoryIds={form.watch('additionalCategoryIds') ?? []}
                      disabled={submitting}
                      onChange={({ primaryCategoryId, additionalCategoryIds }) => {
                        field.onChange(primaryCategoryId);
                        form.setValue('additionalCategoryIds', additionalCategoryIds, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      {...(fieldState.error?.message
                        ? { errorMessage: t(fieldState.error.message, { defaultValue: fieldState.error.message }) }
                        : {})}
                    />
                  )}
                />
              </FieldRow>

              {/* A-2: 브랜드 선택기 — GET /catalog/brands 목록으로 채우고 선택한 brandId 를 전송.
                  미선택 시 brandId=null(detailMapper omit). 다국어 브랜드 표기는 '다국어' 탭의 brandName. */}
              <FieldRow
                label={t('products.fields.brand', '브랜드')}
                hint={t('products.fields.brandHint', '브랜드 마스터에서 선택합니다. 언어별 표기는 다국어 탭에서 입력하세요.')}
              >
                <Controller
                  control={form.control}
                  name="brandId"
                  render={({ field, fieldState }) => (
                    <BrandSelector
                      value={field.value ?? null}
                      disabled={submitting}
                      {...(fieldState.error?.message
                        ? { errorMessage: t(fieldState.error.message, { defaultValue: fieldState.error.message }) }
                        : {})}
                      onChange={(brandId) => field.onChange(brandId)}
                    />
                  )}
                />
              </FieldRow>

              <div id={ARTIST_FIELD_ID} className="scroll-mt-24">
              <FieldRow label={t('products.fields.artist', '연결 아티스트')} hint={t('products.fields.artistHint', '아티스트와 연결된 상품일 때만 선택')}>
                <Controller
                  control={form.control}
                  name="artistId"
                  render={({ field, fieldState }) => (
                    <ArtistSelector
                      value={field.value ?? null}
                      vendorId={vendorId}
                      disabled={submitting}
                      {...(fieldState.error?.message
                        ? { errorMessage: t(fieldState.error.message, { defaultValue: fieldState.error.message }) }
                        : artistServerError
                          ? { errorMessage: artistServerError }
                          : {})}
                      onChange={(next) => {
                        field.onChange(next);
                        setArtistServerError(null);
                        if (next === null) {
                          form.setValue('artistMemberIds', null, { shouldDirty: true });
                        }
                        // QA-492 — 상품의 표시용 아티스트 이름(artistName)은 연결과 별개로 저장된다(BE 는 연결만 끊고 이름은 보존).
                        //  폼은 불러온 이름을 그대로 다시 보내 목록·쇼핑몰에 옛 아티스트가 남았다 → 바꾸거나 지우면 이름도 비운다
                        //  (지우면 "" = 이름 삭제, 다른 아티스트를 고르면 BE 가 그 아티스트 이름으로 채운다).
                        form.setValue('artistName', artistNameAfterArtistChange(next), { shouldDirty: true });
                      }}
                    />
                  )}
                />
              </FieldRow>
              </div>

              {/* L-1: 그룹 멤버 선택은 현재 미사용 — UI만 숨김(폼 필드/로직은 유지). */}
              {SHOW_ARTIST_MEMBERS ? (
                <FieldRow label={t('products.fields.artistMembers', '참여 멤버 (GROUP)')} hint={t('products.fields.artistMembersHintInline', 'GROUP 아티스트에서 참여 멤버를 선택')}>
                  <Controller
                    control={form.control}
                    name="artistMemberIds"
                    render={({ field, fieldState }) => (
                      <ArtistMembersSelector
                        artistId={form.watch('artistId') ?? null}
                        value={field.value ?? null}
                        disabled={submitting}
                        {...(fieldState.error?.message
                          ? { errorMessage: t(fieldState.error.message, { defaultValue: fieldState.error.message }) }
                          : {})}
                        onChange={(next) => field.onChange(next)}
                      />
                    )}
                  />
                </FieldRow>
              ) : null}

              {/* QA-231 — 상품 코드는 서버 자동배정(read-only). 복제 시 -COPY 접미사 폐지(모든 상품 고유 자동 부여).
                  자체상품코드(앨범별 고정 코드)는 별도 선택 입력 필드로 분리한다. */}
              <FieldGrid cols={2}>
                <FieldRow
                  label={t('products.fields.productCode', '상품 코드')}
                  hint={t('products.fields.productCodeAutoHint', '상품 코드는 시스템이 자동으로 부여합니다. 복제·등록 시에도 자동으로 고유 코드가 배정됩니다.')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="productCode"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        readOnly
                        disabled
                        maxLength={50}
                        placeholder={t('products.fields.productCodeAutoPlaceholder', '자동 부여')}
                        aria-label={t('products.fields.productCode', '상품 코드')}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow
                  label={t('products.fields.selfProductCode', '자체상품코드')}
                  hint={t('products.fields.selfProductCodeHint', '앨범별 고정 코드입니다. 복제·등록 시 그대로 유지됩니다. (선택)')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="selfProductCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            maxLength={50}
                            placeholder={t('products.fields.selfProductCodePlaceholder', '예: L200003631')}
                            aria-label={t('products.fields.selfProductCode', '자체상품코드')}
                          />
                        </FormControl>
                        <FormMessage>
                          {form.formState.errors.selfProductCode?.message
                            ? t(form.formState.errors.selfProductCode.message, {
                                defaultValue: form.formState.errors.selfProductCode.message,
                              })
                            : null}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </FieldRow>
              </FieldGrid>

              {auditTimes ? (
                <FieldGrid cols={2}>
                  <FieldRow label={t('products.fields.createdAt', '등록일')} layout="vertical">
                    <Input
                      value={formatAuditTime(auditTimes.createdAt) ?? '—'}
                      readOnly
                      disabled
                      aria-label={t('products.fields.createdAt', '등록일')}
                    />
                  </FieldRow>
                  <FieldRow label={t('products.fields.updatedAt', '수정일')} layout="vertical">
                    <Input
                      value={formatAuditTime(auditTimes.updatedAt) ?? '—'}
                      readOnly
                      disabled
                      aria-label={t('products.fields.updatedAt', '수정일')}
                    />
                  </FieldRow>
                  {/* QA-515 등록자·수정자 — 관리자 이름(로그인 ID). 기록이 없는(이관·반영 전) 상품은 "—". */}
                  <FieldRow label={t('products.fields.createdBy', '등록자')} layout="vertical">
                    <div className={AUDIT_ADMIN_BOX} style={AUDIT_ADMIN_BOX_STYLE} data-testid="product-created-by">
                      <AdminNameCell adminUserId={auditTimes.createdBy} withLoginId />
                    </div>
                  </FieldRow>
                  <FieldRow label={t('products.fields.updatedBy', '수정자')} layout="vertical">
                    <div className={AUDIT_ADMIN_BOX} style={AUDIT_ADMIN_BOX_STYLE} data-testid="product-updated-by">
                      <AdminNameCell adminUserId={auditTimes.updatedBy} withLoginId />
                    </div>
                  </FieldRow>
                </FieldGrid>
              ) : null}

              <FieldGrid cols={2}>
                <FieldRow label={t('products.fields.barcode', '바코드')} layout="vertical">
                  <FormField
                    control={form.control}
                    name="barcode"
                    render={({ field }) => (
                      <Input {...field} value={field.value ?? ''} maxLength={50} placeholder="8801234567890" />
                    )}
                  />
                </FieldRow>
              </FieldGrid>

              <FieldGrid cols={2}>
                <FieldRow
                  label={t('products.fields.originCountry', '원산지')}
                  hint={t('products.fields.originCountryHint', 'ISO 2자 (KR / JP / US 등)')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="originCountry"
                    render={({ field }) => (
                      <Input {...field} value={field.value ?? ''} maxLength={2} placeholder="KR" className="uppercase" />
                    )}
                  />
                </FieldRow>
                <FieldRow label={t('products.fields.manufacturedYearMonth', '제조 년월')} hint="YYYY-MM" layout="vertical">
                  <FormField
                    control={form.control}
                    name="manufacturedYearMonth"
                    render={({ field }) => (
                      <Input {...field} value={field.value ?? ''} placeholder="2026-03" />
                    )}
                  />
                </FieldRow>
              </FieldGrid>

              <FieldRow label={t('products.fields.manufacturerName', '제조사')}>
                <FormField
                  control={form.control}
                  name="manufacturerName"
                  render={({ field }) => (
                    <Input {...field} value={field.value ?? ''} maxLength={150} />
                  )}
                />
              </FieldRow>

              <FieldGrid cols={2}>
                {/* QA-334·QA-458 — 발매일은 날짜만. 문구 발매일은 간단 설명(고도몰도 짧은 설명에 "발매일 : …"을 적었다). */}
                <FieldRow
                  label={t('products.fields.releaseDate', '발매일')}
                  hint={t('products.fields.releaseDateHint', '날짜가 정해졌을 때만 입력하세요. “2026년 8월 18일부터 순차 출고 예정”처럼 문구로 쓰려면 발매일은 비우고 [다국어 명칭/설명/상세] 한국어 탭의 간단 설명에 “발매일 : …”으로 적어 주세요. 둘 다 적으면 상세에 발매일이 두 줄로 보입니다.')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="releaseDate"
                    render={({ field }) => <Input type="date" {...field} value={field.value ?? ''} />}
                  />
                </FieldRow>
                <FieldRow label={t('products.fields.stockInboundDate', '입고 예정일')} layout="vertical">
                  <FormField
                    control={form.control}
                    name="stockInboundDate"
                    render={({ field }) => <Input type="date" {...field} value={field.value ?? ''} />}
                  />
                </FieldRow>
              </FieldGrid>
            </div>
          </SectionCard>

          {/* 2. 다국어 명칭 / 설명 / 상세 본문
              상세 본문(detailHtml)을 로케일 탭(한국어 정본 + en/ja/zh)으로 이 섹션에 일원화했다. */}
          <SectionCard
            id="section-translations"
            index={2}
            title={t('products.sections.translations', '다국어 명칭/설명/상세')}
            description={t('products.sections.translationsDescription', '한국어/영문/일본어/중국어 명칭·설명·상세 본문을 입력합니다.')}
          >
            <TranslationsTabs showEventSubtitle={showEventSubtitle} />
          </SectionCard>

          {/* 3. 판매 가격 */}
          <SectionCard
            id="section-pricing"
            index={3}
            title={t('products.sections.pricing', '판매 가격')}
            description={t('products.sections.pricingDescription', '원화(KRW) 기준가 필수 · 외화는 비우면 환율 자동계산')}
          >
            <div className="space-y-3">
              {/* KRW 기준 통화 — 항상 필수 입력 */}
              <div
                className="grid grid-cols-[80px_1fr_1fr] items-end gap-3"
                data-testid="pricing-row-KRW"
              >
                <div
                  className="flex h-11 items-center justify-center rounded-md text-xs font-semibold tabular-nums"
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px solid var(--divider-strong)',
                    color: 'var(--content-muted)',
                  }}
                >
                  {CURRENCY_LABELS.KRW}
                </div>
                <FormField
                  control={form.control}
                  name="prices.KRW.listAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('products.fields.listPrice', '정가')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step={CURRENCY_STEP.KRW}
                          inputMode="numeric"
                          required
                          aria-required="true"
                          aria-label={`${CURRENCY_LABELS.KRW} ${t('products.fields.listPrice', '정가')}`}
                          {...field}
                          value={field.value ?? 0}
                          onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="prices.KRW.saleAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('products.fields.salePrice', '판매가')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step={CURRENCY_STEP.KRW}
                          inputMode="numeric"
                          required
                          aria-required="true"
                          aria-label={`${CURRENCY_LABELS.KRW} ${t('products.fields.salePrice', '판매가')}`}
                          {...field}
                          value={field.value ?? 0}
                          onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <p className="pt-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                외화 가격을 비워 두면 환율 설정의 환율로 자동 계산됩니다. 값을 입력하면 해당 통화는 고정값으로 노출됩니다.
              </p>

              {/* 외화 오버라이드 — 비우면 자동계산, 입력하면 고정값 */}
              {OVERRIDE_CURRENCIES.map((currency) => {
                const rate = krwPerUnitByCurrency.get(currency);
                const listPreview = autoConvert(krwListAmount, rate, currency);
                const salePreview = autoConvert(krwSaleAmount, rate, currency);
                return (
                  <div
                    key={currency}
                    className="grid grid-cols-[80px_1fr_1fr] items-end gap-3"
                    data-testid={`pricing-row-${currency}`}
                  >
                    <div
                      className="flex h-11 items-center justify-center rounded-md text-xs font-semibold tabular-nums"
                      style={{
                        background: 'var(--surface-1)',
                        border: '1px solid var(--divider-strong)',
                        color: 'var(--content-muted)',
                      }}
                    >
                      {CURRENCY_LABELS[currency]}
                    </div>
                    <FormField
                      control={form.control}
                      name={`prices.${currency}.listAmount`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              step={CURRENCY_STEP[currency]}
                              inputMode="decimal"
                              aria-label={`${CURRENCY_LABELS[currency]} ${t('products.fields.listPrice', '정가')}`}
                              placeholder={listPreview ? `자동 ${listPreview}` : '자동계산'}
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`prices.${currency}.saleAmount`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              step={CURRENCY_STEP[currency]}
                              inputMode="decimal"
                              aria-label={`${CURRENCY_LABELS[currency]} ${t('products.fields.salePrice', '판매가')}`}
                              placeholder={salePreview ? `자동 ${salePreview}` : '자동계산'}
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                              }
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                );
              })}

              {/* 부가세 과세/면세 */}
              <FieldRow
                label={t('products.fields.taxExempt', '부가세')}
                hint={t('products.fields.taxExemptHint', '면세 상품이면 체크 (기본: 과세 10%)')}
              >
                <Controller
                  control={form.control}
                  name="taxExempt"
                  render={({ field }) => (
                    <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                      <input
                        type="checkbox"
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        aria-label={t('products.fields.taxExempt', '부가세')}
                      />
                      {field.value ?? false
                        ? t('products.fields.taxExemptOn', '면세 (부가세 없음)')
                        : t('products.fields.taxExemptOff', '과세 (부가세 10%)')}
                    </label>
                  )}
                />
              </FieldRow>
            </div>
          </SectionCard>

          {/* 4. 옵션 / 재고 — Task#4: 옵션은 선택사항. G2: 상단 "옵션 구성"(방식 토글 + 그룹 생성기)
              + 하단 단일 "SKU 목록"(평면·조합·등록 미리보기 병합 테이블)으로 일원화. */}
          <SectionCard id="section-variants" index={4} title={t('products.sections.variants', '옵션 / 재고')}>
            {form.formState.errors.variants?.root?.message && (
              <p className="mb-3 text-sm font-medium" style={{ color: 'var(--status-danger-fg)' }}>
                {t(form.formState.errors.variants.root.message, { defaultValue: form.formState.errors.variants.root.message })}
              </p>
            )}

            {/* 옵션명(catalog V135) — FE 옵션 선택칸 위 이름(고도몰 optionName, 예 "MEMBER"). BE 가 등록(POST)·전체 수정(PUT)
                에선 받지 않아 수정 화면에서만 노출하고, 바뀐 경우에만 PATCH 로 저장한다(ProductEditPage). */}
            {isEditMode ? (
              <div className="mb-5">
                <FieldRow
                  label={t('products.fields.optionName', '옵션명')}
                  hint={t('products.fields.optionNameHint', '고객 화면의 옵션 선택칸 위에 보이는 이름입니다 (예: MEMBER). 비우면 "옵션"으로 보입니다.')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="optionName"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        maxLength={255}
                        placeholder="MEMBER"
                        data-testid="product-option-name"
                      />
                    )}
                  />
                </FieldRow>
              </div>
            ) : null}

            {/* 상단 — 옵션 구성(방식 토글 + 조합 모드 그룹 생성기). */}
            <div className="flex flex-col gap-3">
              <h4 className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                {t('products.optionGroups.optionConfigTitle', '옵션 구성')}
              </h4>
              {isEditMode && optionGroupsQuery.isPending ? (
                <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
                  {t('products.optionGroups.loading', { defaultValue: '조합 옵션을 불러오는 중...' })}
                </p>
              ) : (
                <OptionConfigBlock
                  control={form.control}
                  mode={optionMode}
                  onModeChange={changeOptionMode}
                  isEditMode={isEditMode}
                  disabled={submitting}
                />
              )}
            </div>

            {optionModeBlocked ? (
              <p className="mt-2 text-[12px]" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
                {optionModeBlocked}
              </p>
            ) : null}

            {/* QA-521 — 옵션이 없는 상품: 재고 칸만. */}
            {optionMode === 'none' && flatEntries[0] ? (
              <div className="mt-6 border-t pt-5" style={{ borderColor: 'var(--divider-soft)' }}>
                <h4 className="mb-3 text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                  {t('products.optionGroups.noOptionStockTitle', '재고')}
                </h4>
                <NoOptionStockFields form={form} index={flatEntries[0].index} isEditMode={isEditMode} />
              </div>
            ) : null}

            {/* 하단 — SKU 목록(단일 테이블: 평면 + 조합 + 등록 미리보기 병합). */}
            <div
              className={optionMode === 'none' && flatEntries[0] ? 'hidden' : 'mt-6 border-t pt-5'}
              style={{ borderColor: 'var(--divider-soft)' }}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <h4 className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                  {t('products.optionGroups.skuListTitle', 'SKU 목록')}
                </h4>
                {/* 단일 모드만 평면 옵션 추가. 조합 모드(그룹 활성)는 혼용 금지로 추가 차단. */}
                {optionMode === 'single' && !optionGroupsActive ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => variants.append({ label: '', initialStockQty: 0, stockTrackingEnabled: false })}
                  >
                    {t('products.actions.addVariant', '옵션 추가')}
                  </Button>
                ) : null}
              </div>

              {variants.fields.length === 0 && !optionGroupsActive ? (
                <p
                  className="rounded-md px-4 py-3 text-[13px]"
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px dashed var(--divider-strong)',
                    color: 'var(--content-muted)',
                  }}
                >
                  {t(
                    'products.optionGroups.skuListEmpty',
                    '옵션 없이 판매하는 상품입니다 — 저장 시 기본 옵션이 자동 생성됩니다. 옵션이 필요하면 옵션 구성에서 추가하세요.',
                  )}
                </p>
              ) : (
                <SkuTable
                  form={form}
                  flatEntries={flatEntries}
                  comboEntries={comboEntries}
                  onRemoveFlat={(idx) => variants.remove(idx)}
                  isEditMode={isEditMode}
                />
              )}

              {optionGroupsActive ? (
                <p className="mt-3 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  {t('products.optionGroups.flatAddBlocked', {
                    defaultValue: '조합 옵션 사용 중 — 일반 옵션은 추가할 수 없습니다(혼용 금지).',
                  })}
                </p>
              ) : null}
            </div>

            {/* 1인 구매 수량 제한 (상품 단위). 비우면 제한 없음. */}
            <div className="mt-6 border-t pt-5" style={{ borderColor: 'var(--divider-soft)' }}>
              <h4 className="mb-3 text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                {t('products.fields.purchaseLimitTitle', '1인 구매 수량 제한')}
              </h4>
              {/* QA-542 구매수량 기준(고도몰 fixedOrderCnt) — 최소·최대를 무엇 단위로 셀지. */}
              <FieldRow label={t('products.fields.purchaseLimitBasis', '구매수량 기준')} layout="vertical">
                <FormField
                  control={form.control}
                  name="purchaseLimitBasis"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <RadioGroup
                        value={field.value ?? 'OPTION'}
                        onValueChange={(v) => field.onChange(v as 'OPTION' | 'PRODUCT' | 'MEMBER')}
                        orientation="horizontal"
                        aria-label={t('products.fields.purchaseLimitBasis', '구매수량 기준')}
                        data-testid="product-purchase-limit-basis"
                        options={[
                          { value: 'OPTION', label: t('products.fields.purchaseLimitBasisOption', '옵션 기준') },
                          { value: 'PRODUCT', label: t('products.fields.purchaseLimitBasisProduct', '상품 기준') },
                          { value: 'MEMBER', label: t('products.fields.purchaseLimitBasisMember', '아이디 기준') },
                        ]}
                      />
                      <p className="text-[12px]" style={{ color: 'var(--content-muted)' }} data-testid="product-purchase-limit-basis-hint">
                        {(field.value ?? 'OPTION') === 'OPTION'
                          ? t('products.fields.purchaseLimitBasisOptionHint', '주문의 옵션 하나마다 최소·최대 수량을 셉니다.')
                          : (field.value ?? 'OPTION') === 'PRODUCT'
                            ? t('products.fields.purchaseLimitBasisProductHint', '한 주문에서 이 상품의 모든 옵션 수량을 합쳐 셉니다.')
                            : t(
                                'products.fields.purchaseLimitBasisMemberHint',
                                '아이디 기준은 회원 누적 구매 수량으로 제한합니다(여러 번 나눠 사도 최대 수량을 넘을 수 없음). 비회원은 한 주문 수량으로만 제한되니, 반복 구매를 막으려면 구매 권한을 회원 전용으로 설정해 주세요.',
                              )}
                      </p>
                    </div>
                  )}
                />
              </FieldRow>
              <FieldGrid cols={2}>
                <FieldRow
                  label={t('products.fields.purchaseLimitMin', '최소 구매 수량')}
                  hint={t('products.fields.purchaseLimitHint', '비우면 제한 없음')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="purchaseLimitMin"
                    render={({ field }) => (
                      <Input
                        type="number"
                        min={1}
                        step="1"
                        inputMode="numeric"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          field.onChange(Number.isFinite(next) ? next : null);
                        }}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow
                  label={t('products.fields.purchaseLimitMax', '최대 구매 수량')}
                  hint={t('products.fields.purchaseLimitHint', '비우면 제한 없음')}
                  layout="vertical"
                >
                  <FormField
                    control={form.control}
                    name="purchaseLimitMax"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            step="1"
                            inputMode="numeric"
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const next = e.target.valueAsNumber;
                              field.onChange(Number.isFinite(next) ? next : null);
                            }}
                          />
                        </FormControl>
                        <FormMessage>
                          {form.formState.errors.purchaseLimitMax?.message
                            ? t(form.formState.errors.purchaseLimitMax.message, {
                                defaultValue: form.formState.errors.purchaseLimitMax.message,
                              })
                            : null}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </FieldRow>
              </FieldGrid>
            </div>
          </SectionCard>

          {/* 5. 상세 페이지 이미지
              상세 본문(detailHtml)은 §2 다국어 탭의 한국어(정본) 에디터로 일원화했다.
              여기서는 대표 썸네일 + 상세 페이지 갤러리(보조 이미지)만 다룬다. */}
          <SectionCard
            id="section-detail"
            index={5}
            title={t('products.sections.detailImages', '상세 페이지 이미지')}
            description={t('products.sections.detailImagesDescription', '대표 썸네일 + 상세 페이지 갤러리 (본문은 §2 다국어 탭)')}
          >
            <div className="flex flex-col gap-6">
              <FieldRow
                label={t('products.fields.thumbnailUrl', '대표 썸네일 (목록 전용)')}
                hint={t(
                  'products.fields.thumbnailHint',
                  '상품 목록·카드·검색결과·OG 이미지로만 사용됩니다. 권장 1:1 정사각. (PDP 메인은 아래 상세 갤러리 첫 장)',
                )}
              >
                <Controller
                  control={form.control}
                  name="thumbnailUrl"
                  render={({ field }) => (
                    <ImageUploadField
                      queue={uploads}
                      name="thumbnailUrl"
                      category="PRODUCT_THUMBNAIL"
                      value={field.value ?? null}
                      onChange={(url) => field.onChange(url)}
                      disabled={submitting}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                label={t('products.fields.detailImages', '상세 이미지 갤러리 (PDP 표시)')}
                hint={t(
                  'products.fields.detailImagesHint',
                  '상품 상세(PDP)에 순서대로 노출되는 사진입니다. 첫 번째 사진이 PDP 대표로 표시되며, ↑↓로 순서를 바꿀 수 있습니다. 비워 두면 대표 썸네일이 대신 노출됩니다.',
                )}
              >
                <Controller
                  control={form.control}
                  name="detailImages"
                  render={({ field }) => (
                    <ImageGalleryField
                      queue={uploads}
                      name="detailImages"
                      category="PRODUCT_DETAIL"
                      value={field.value ?? []}
                      onChange={(urls) => field.onChange(urls)}
                      max={50}
                    />
                  )}
                />
              </FieldRow>
            </div>
          </SectionCard>

          {/* 6. 판매 일정 (A-5 ID83 — 판매/예약 일정 한 화면 일원화)
              종전 §6 판매 일정 + §7 예약판매 설정이 동일한 salesStartAt/salesEndAt/shippingStartAt
              필드를 두 섹션에 중복 노출해 운영자 혼란을 유발했다. 예약판매 상태 셀렉터를 한 섹션
              상단에 두고, 같은 기간/출고 필드의 라벨만 예약 여부에 따라 바꿔 한 번만 노출한다. */}
          <SectionCard
            id="section-schedule"
            index={6}
            title={t('products.sections.schedule', '판매 일정')}
            description={t('products.sections.scheduleDescription', '지정한 일시에 판매가 열리고 닫힙니다')}
          >
            <div className="flex flex-col gap-5">
              {/* QA-594 — [예약판매 상태]·[출고 시작 시각]은 쇼핑몰·주문 동작에 쓰이지 않아(라벨만 바뀜) 운영 혼선만 생겨 숨겼다.
                  저장된 값은 그대로 둔다(폼 값 유지 → 저장 때 같은 값). 실제 동작은 판매 기간: 시작 전 "판매 예정", 종료 후 "판매 종료"로
                  구매가 막힌다(BE 장바구니·주문서도 같은 기준). 판매 상태가 '판매중'이어야 기간 안에 살 수 있다. */}
              <FieldRow
                label={t('products.fields.salesPeriod', '판매 기간')}
                hint={t(
                  'products.fields.salesPeriodHint',
                  '시작 일시부터 구매할 수 있고, 종료 일시가 지나면 "판매 종료"로 구매가 막힙니다. 비우면 제한 없음. 판매 상태가 "판매중"이어야 합니다.',
                )}
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="salesStartAt"
                    render={({ field }) => (
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value ?? ''}
                        aria-label={t('products.fields.salesStart', '판매 시작')}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="salesEndAt"
                    render={({ field }) => (
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value ?? ''}
                        aria-label={t('products.fields.salesEnd', '판매 종료')}
                      />
                    )}
                  />
                </div>
              </FieldRow>
            </div>
          </SectionCard>

          {/* 7. 배송 — ③ 표면 재구성(페이로드/청구 로직 무변경):
              · 1순위 shippingFeePolicyId = 실제 청구 기준 템플릿(order fee-policy 그룹핑 산정).
              · shippingFree = 실제 청구 반영(체크 라인은 주문 배송비 산정에서 제외).
              · weightGrams(①) = 실측 무게(g) — 해외배송비 실무게 산정 입력(V107, null=미변경/0=clear).
              · shippingFeeType/baseFee/islandExtraFee/freeThreshold = PDP "배송안내" 문구 생성용
                표기(SF resolveShippingNotice 2순위) — 청구 금액 미사용이라 접힌 그룹으로 강등. */}
          <SectionCard
            id="section-shipping"
            index={7}
            title={t('products.sections.shipping', '배송')}
            description={t('products.sections.shippingDescription', '배송비 템플릿(청구) · 무게 · PDP 표시용 안내값')}
          >
            <div className="flex flex-col gap-5">
              {/* M-8 ID81 배송비 마스터(fee-policies) — 실제 주문 배송비 산정 기준(주 입력, 최상단).
                  order shipping_fee_policy.policyId 를 product.shippingFeePolicyId(VARCHAR36)로 저장.
                  §정책연결의 shippingPolicyId(배송 안내 텍스트정책)와 별개. */}
              <FieldRow
                label={t('products.fields.shippingFeePolicy', '배송비 조건(템플릿)')}
                hint={t(
                  'products.fields.shippingFeePolicyHint',
                  '실제 주문 배송비는 이 템플릿으로 계산됩니다. 미선택 시 기본 배송비 템플릿이 적용됩니다.',
                )}
              >
                <Controller
                  control={form.control}
                  name="shippingFeePolicyId"
                  render={({ field }) => (
                    <ShippingFeePolicySelect
                      value={field.value ?? null}
                      disabled={submitting}
                      ariaLabel={t('products.fields.shippingFeePolicy', '배송비 조건(템플릿)')}
                      onChange={(policyId) => field.onChange(policyId)}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                label={t('products.fields.shippingFree', '무료 배송')}
                hint={t('products.fields.shippingFreeHint', '체크 시 이 상품은 주문 배송비 산정에서 제외됩니다(실제 청구 반영).')}
              >
                <Controller
                  control={form.control}
                  name="shippingFree"
                  render={({ field }) => (
                    <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                      <input
                        type="checkbox"
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      {t('products.fields.shippingFreeOn', '이 상품은 무료 배송')}
                    </label>
                  )}
                />
              </FieldRow>

              {/* ① Task#7 — 상품 실측 무게(그램). 해외배송비가 가정무게 대신 실무게로 산정되는 입력
                  (be-catalog V107 weightGrams: null=미변경 보존, 0=미설정 clear, 양수=설정). */}
              <FieldRow
                label={t('products.fields.weightGrams', '무게 (g)')}
                hint={t(
                  'products.fields.weightGramsHint',
                  '해외배송비 실무게 산정에 사용됩니다. 비우면 미설정(정책 가정무게로 산정).',
                )}
              >
                <FormField
                  control={form.control}
                  name="weightGrams"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      inputMode="numeric"
                      placeholder={t('products.fields.weightGramsPlaceholder', '예: 350')}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const next = e.target.valueAsNumber;
                        field.onChange(Number.isFinite(next) ? next : null);
                      }}
                    />
                  )}
                />
              </FieldRow>

              {/* ③ PDP 표시용 안내값 — 청구 금액에는 쓰이지 않는 표기 그룹(접힘). SF 가 이 값으로
                  상품페이지 "배송안내" 문구를 자동 생성한다(수동 배송안내 입력 시 그쪽이 우선). */}
              <details
                className="rounded-md"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
                data-testid="shipping-display-group"
              >
                <summary
                  className="cursor-pointer select-none px-4 py-3 text-[13px] font-medium"
                  style={{ color: 'var(--content-strong)' }}
                >
                  {t('products.fields.shippingDisplayGroup', 'PDP 표시용 안내값 (청구 금액 아님)')}
                </summary>
                <div className="flex flex-col gap-5 px-4 pb-4 pt-1">
                  <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {t(
                      'products.fields.shippingDisplayGroupHint',
                      '상품페이지 “배송안내” 문구 생성에만 사용됩니다. 실제 청구 배송비는 위 배송비 조건(템플릿)으로 계산됩니다.',
                    )}
                  </p>
                  <FieldRow label={t('products.fields.shippingFeeType', '배송비 표기 유형')}>
                    <Controller
                      control={form.control}
                      name="shippingFeeType"
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value ?? 'FREE'}
                          onValueChange={(v) => field.onChange(v as ShippingFeeType)}
                          orientation="horizontal"
                          aria-label={t('products.fields.shippingFeeType', '배송비 표기 유형')}
                          options={SHIPPING_FEE_TYPE_OPTIONS.map((opt) => ({
                            value: opt.value,
                            label: t(`products.shippingFeeType.${opt.value}`, opt.label),
                          }))}
                        />
                      )}
                    />
                  </FieldRow>

                  {shippingFeeType === 'FLAT' || shippingFeeType === 'REGION' ? (
                    <FieldRow label={t('products.fields.baseFee', '기본 배송비')} hint="원">
                      <FormField
                        control={form.control}
                        name="baseFee"
                        render={({ field }) => (
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            inputMode="numeric"
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const next = e.target.valueAsNumber;
                              field.onChange(Number.isFinite(next) ? next : null);
                            }}
                          />
                        )}
                      />
                    </FieldRow>
                  ) : null}

                  {shippingFeeType === 'REGION' ? (
                    <FieldRow label={t('products.fields.islandExtraFee', '도서산간 추가비')} hint="원">
                      <FormField
                        control={form.control}
                        name="islandExtraFee"
                        render={({ field }) => (
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            inputMode="numeric"
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const next = e.target.valueAsNumber;
                              field.onChange(Number.isFinite(next) ? next : null);
                            }}
                          />
                        )}
                      />
                    </FieldRow>
                  ) : null}

                  {shippingFeeType === 'CONDITIONAL' ? (
                    <FieldRow
                      label={t('products.fields.freeThreshold', '무료 기준액')}
                      hint={t('products.fields.freeThresholdHint', '이 금액 이상 주문 시 무료 (원)')}
                    >
                      <FormField
                        control={form.control}
                        name="freeThreshold"
                        render={({ field }) => (
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            inputMode="numeric"
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const next = e.target.valueAsNumber;
                              field.onChange(Number.isFinite(next) ? next : null);
                            }}
                          />
                        )}
                      />
                    </FieldRow>
                  ) : null}
                </div>
              </details>

              <FieldGrid cols={2}>
                <FieldRow label={t('products.fields.returnFee', '반품 배송비')} hint="원" layout="vertical">
                  <FormField
                    control={form.control}
                    name="returnFee"
                    render={({ field }) => (
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        inputMode="numeric"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          field.onChange(Number.isFinite(next) ? next : null);
                        }}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow label={t('products.fields.exchangeFee', '교환 배송비')} hint="원" layout="vertical">
                  <FormField
                    control={form.control}
                    name="exchangeFee"
                    render={({ field }) => (
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        inputMode="numeric"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const next = e.target.valueAsNumber;
                          field.onChange(Number.isFinite(next) ? next : null);
                        }}
                      />
                    )}
                  />
                </FieldRow>
              </FieldGrid>

              {/* 항목2 — 해외배송(영문/중문/일문몰) 표시 전용 블록. 해외배송은 몰별 글로벌
                  정책이라 상품별 선택/저장이 없다(catalog 스키마에 해외몰 노출 필드 부재).
                  활성 정책 구성 여부만 요약하고 통합관리로 안내한다(policyId 미노출). */}
              <FieldRow
                label={t('products.fields.overseasShipping', '해외배송')}
                hint={t('products.fields.overseasShippingHint', '몰별 공통 정책 — 상품별 개별 설정은 없습니다.')}
              >
                <OverseasShippingSummary />
              </FieldRow>
            </div>
          </SectionCard>

          {/* 8. 정책 연결 */}
          <SectionCard
            id="section-policy"
            index={8}
            title={t('products.sections.policy', '정책 연결')}
            description={t('products.sections.policyDescription', '미선택 시 기본 정책이 적용됩니다.')}
          >
            <div className="flex flex-col gap-5">
              <FieldRow label={t('products.fields.shippingPolicy', '배송 안내 정책')}>
                <Controller
                  control={form.control}
                  name="shippingPolicyId"
                  render={({ field }) => (
                    <PolicySelect
                      type="SHIPPING"
                      value={field.value ?? null}
                      disabled={submitting}
                      placeholder={t('products.fields.policyUnset', '기본 정책 사용')}
                      ariaLabel={t('products.fields.shippingPolicy', '배송 안내 정책')}
                      onChange={(policyId) => field.onChange(policyId)}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow label={t('products.fields.guidePolicy', '안내 사항 정책')}>
                <Controller
                  control={form.control}
                  name="guidePolicyId"
                  render={({ field }) => (
                    <PolicySelect
                      type="GUIDE"
                      value={field.value ?? null}
                      disabled={submitting}
                      placeholder={t('products.fields.policyUnset', '기본 정책 사용')}
                      ariaLabel={t('products.fields.guidePolicy', '안내 사항 정책')}
                      onChange={(policyId) => field.onChange(policyId)}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow label={t('products.fields.operationPolicy', '운영 방침 정책')}>
                <Controller
                  control={form.control}
                  name="operationPolicyId"
                  render={({ field }) => (
                    <PolicySelect
                      type="OPERATION"
                      value={field.value ?? null}
                      disabled={submitting}
                      placeholder={t('products.fields.policyUnset', '기본 정책 사용')}
                      ariaLabel={t('products.fields.operationPolicy', '운영 방침 정책')}
                      onChange={(policyId) => field.onChange(policyId)}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                label="마일리지 적립 정책"
                hint="미선택 시 전역 기본 적립 정책이 적용됩니다."
              >
                <EarnPolicySelect
                  value={earnPolicyId}
                  disabled={submitting}
                  placeholder="기본 정책 사용"
                  ariaLabel="마일리지 적립 정책"
                  onChange={setEarnPolicyId}
                />
              </FieldRow>
              {/* QA-456 2단계 — 마일리지 지급 대상(고도몰 [마일리지 설정 → 지급 대상]). 판정은 BE. */}
              <FieldRow
                label={t('products.fields.mileageTarget', '마일리지 지급 대상')}
                hint={t('products.fields.mileageTargetHint', '특정회원등급이면 고른 등급에게만, 지급 안 함이면 아무에게도 적립하지 않습니다.')}
              >
                <div className="flex flex-col gap-3" data-testid="mileage-target">
                  <Controller
                    control={form.control}
                    name="mileageTarget"
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? 'ALL'}
                        onValueChange={field.onChange}
                        orientation="horizontal"
                        aria-label={t('products.fields.mileageTarget', '마일리지 지급 대상')}
                        options={[
                          { value: 'ALL', label: t('products.fields.mileageTargetAll', '전체회원') },
                          { value: 'GRADE', label: t('products.fields.mileageTargetGrade', '특정회원등급') },
                          { value: 'NONE', label: t('products.fields.mileageTargetNone', '지급 안 함') },
                        ]}
                      />
                    )}
                  />
                  {form.watch('mileageTarget') === 'GRADE' ? (
                    <MemberGradePicker
                      control={form.control}
                      name="mileageGradeIds"
                      label={t('products.fields.mileageGrades', '마일리지 지급 등급')}
                      testId="mileage-grade-ids"
                    />
                  ) : null}
                </div>
              </FieldRow>
            </div>
          </SectionCard>

          {/* 9. 검색 · 노출 최적화 (A-2 commerceMeta) */}
          <SectionCard
            id="section-seo"
            index={9}
            title={t('products.sections.seo', '검색 · 노출 최적화')}
            description={t('products.sections.seoDescription', '검색 키워드 · 정렬 가중치 · SEO 메타 · 연관 상품')}
          >
            <div className="flex flex-col gap-5">
              <FieldRow
                label={t('products.fields.searchKeywords', '검색 키워드 / 태그')}
                hint={t('products.fields.searchKeywordsHint', '쉼표(,)로 구분해 입력 (예: 굿즈, 한정판, 키링)')}
              >
                <Controller
                  control={form.control}
                  name="searchKeywords"
                  render={({ field }) => (
                    <ProductKeywordsField
                      value={field.value}
                      maxLength={SEARCH_KEYWORD_MAX}
                      disabled={submitting}
                      placeholder="굿즈, 한정판, 키링"
                      onChange={(keywords) => field.onChange(keywords.length > 0 ? keywords : [])}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                label={t('products.fields.sortWeight', '정렬 가중치')}
                hint={t('products.fields.sortWeightHint', '클수록 목록 상단 우선 노출 (비우면 기본 0)')}
              >
                <FormField
                  control={form.control}
                  name="sortWeight"
                  render={({ field }) => (
                    <Input
                      type="number"
                      step="1"
                      inputMode="numeric"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const next = e.target.valueAsNumber;
                        field.onChange(Number.isFinite(next) ? next : null);
                      }}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow label={t('products.fields.seoTitle', 'SEO 메타 타이틀')}>
                <FormField
                  control={form.control}
                  name="seoTitle"
                  render={({ field }) => (
                    <Input {...field} value={field.value ?? ''} maxLength={255} placeholder={t('products.fields.seoTitlePlaceholder', '검색엔진 노출용 제목 (미입력 시 상품명 사용)')} />
                  )}
                />
              </FieldRow>

              <FieldRow label={t('products.fields.seoDescription', 'SEO 메타 설명')}>
                <FormField
                  control={form.control}
                  name="seoDescription"
                  render={({ field }) => (
                    <Input {...field} value={field.value ?? ''} maxLength={500} placeholder={t('products.fields.seoDescriptionPlaceholder', '검색결과에 표시될 설명 (최대 500자)')} />
                  )}
                />
              </FieldRow>

              <FieldRow
                label={t('products.fields.relatedProducts', '연관 / 추천 상품')}
                hint={t('products.fields.relatedProductsHint', 'PDP 하단에 함께 노출할 상품을 선택')}
              >
                <Controller
                  control={form.control}
                  name="relatedProductIds"
                  render={({ field }) => (
                    <ProductMultiSelectModalField
                      value={field.value ?? []}
                      disabled={submitting}
                      onChange={(ids) => field.onChange(ids.length > 0 ? ids : [])}
                      emptyHint={t('products.fields.relatedProductsEmpty', '선택 안 함')}
                      testId="related-products-field"
                    />
                  )}
                />
              </FieldRow>
            </div>
          </SectionCard>

          {/* 10. 이벤트 연결 */}
          <SectionCard
            id="section-events"
            index={10}
            title={t('products.sections.events', '이벤트 연결')}
            description={t('products.sections.eventsDescription', '저장 시 이벤트 연결이 함께 갱신됩니다.')}
          >
            <ProductEventsField
              value={eventLinks}
              disabled={submitting}
              onChange={setEventLinks}
            />
            <div className="mt-6 border-t pt-5" style={{ borderColor: 'var(--divider-soft)' }}>
              <h4
                className="mb-3 text-[14px] font-semibold"
                style={{ color: 'var(--content-strong)' }}
              >
                {t('products.coupons.title', '적용 가능 쿠폰')}
              </h4>
              <ProductCouponsField
                value={couponLinks}
                disabled={submitting}
                onChange={setCouponLinks}
              />
            </div>
          </SectionCard>

          {/* 11. 상품 정보 고시 */}
          <SectionCard
            id="section-notice"
            index={11}
            title={t('products.sections.noticeSpec', '상품 정보 고시')}
            description={t('products.sections.noticeSpecDescription', '품목별 고시 (원산지/제조자/A/S 책임자 등) 자유롭게 입력')}
          >
            <NoticeSpecEditor />
          </SectionCard>

          {/* 12. 추가 입력 · 동의 (커스텀 폼 빌더 + 가변 동의, 항목2: 4몰별 분리) */}
          <SectionCard
            id="section-collection"
            index={12}
            title={t('products.sections.collection', '추가 입력 · 동의')}
            description={t('products.sections.collectionDescription', '상품별 커스텀 입력 필드와 개별 동의 항목을 4몰(국내/일문/중문/영문)별로 구성합니다.')}
          >
            {/* QA-507 공통 동의 4종 사용 여부(문구는 동의 문구 관리). */}
            <Controller
              control={form.control}
              name="commonConsentCodes"
              render={({ field }) => (
                <ProductCommonConsentsField value={field.value ?? []} disabled={submitting} onChange={(next) => field.onChange(next)} />
              )}
            />
            <ProductCollectionMallTabs
              value={collectionByMall}
              disabled={submitting}
              onChange={updateCollectionMall}
            />
          </SectionCard>

          {footerSlot === false ? null : footerSlot !== undefined ? footerSlot : (
            <div className="flex items-center justify-end gap-2 pt-2">
              {onCancel && (
                <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                  {t('products.actions.cancel')}
                </Button>
              )}
              <Button
                type="submit"
                variant="cta"
                disabled={
                  submitting ||
                  uploads.isUploading ||
                  (form.formState.isSubmitted && !form.formState.isValid)
                }
              >
                {uploads.isUploading ? t('products.actions.uploading', '이미지 업로드 중…') : t(submitLabelKey)}
              </Button>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
};
